import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Menu, X, Home, BookOpen, Calendar, Archive, User, Mail, MessageSquare, LogIn, LogOut, Settings, Heart, Search, Package } from "lucide-react";
import { useTips } from "@/hooks/useTips";
import { SearchBar } from "@/components/SearchBar";

const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const { tips } = useTips();
  const { user, signOut, isAdmin } = useAuth();

  const handleSignOut = async () => {
    await signOut();
    setIsOpen(false);
  };

  // Get the latest tip ID
  const latestTip = tips?.filter(tip => tip.is_published)
    .sort((a, b) => new Date(b.published_at || b.created_at).getTime() - new Date(a.published_at || a.created_at).getTime())[0];

  const navItems = [
    { name: "Home", path: "/", icon: Home },
    { name: "Search", path: "/search", icon: Search },
    ...(user ? [{ name: "My Favorites", path: "/favorites", icon: Heart }] : []),
    { name: "Today's Tip", path: latestTip ? `/tips/${latestTip.id}` : "/archive", icon: Calendar },
    { name: "Archive", path: "/archive", icon: Archive },
    { name: "Experiences", path: "/experiences", icon: MessageSquare },
    { name: "About", path: "/about", icon: BookOpen },
    { name: "Products", path: "/products", icon: Package },
    ...(isAdmin ? [{ name: "Admin", path: "/admin", icon: Settings }] : []),
  ];

  const isActive = (path: string) => location.pathname === path;

  const closeSheet = () => setIsOpen(false);

  return (
    <nav className="sticky top-0 z-50 bg-background/80 backdrop-blur-md border-b border-border/20">
      <div className="max-w-6xl mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2 group">
            <div className="text-2xl font-bold bg-gradient-primary bg-clip-text text-transparent group-hover:scale-105 transition-transform">
              Small Steps Forward
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-1">
            {navItems.map((item) => (
              <Button
                key={item.name}
                asChild
                variant={isActive(item.path) ? "default" : "ghost"}
                className={`
                  hover:bg-primary/10 hover:text-primary transition-all duration-200
                  ${isActive(item.path) ? "bg-primary/20 text-primary" : ""}
                `}
              >
                <Link to={item.path} className="flex items-center space-x-2">
                  <item.icon className="h-4 w-4" />
                  <span>{item.name}</span>
                </Link>
              </Button>
            ))}
            
            {/* Auth Actions */}
            {user ? (
              <div className="flex items-center space-x-4 ml-4">
                <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                  <User className="h-4 w-4" />
                  <span>{user.email}</span>
                </div>
                <Button
                  onClick={handleSignOut}
                  variant="outline"
                  size="sm"
                  className="flex items-center space-x-2"
                >
                  <LogOut className="h-4 w-4" />
                  <span>Sign Out</span>
                </Button>
              </div>
            ) : (
              <Button asChild variant="gradient" size="sm" className="ml-4">
                <Link to="/auth" className="flex items-center space-x-2">
                  <LogIn className="h-4 w-4" />
                  <span>Sign In</span>
                </Link>
              </Button>
            )}
          </div>

          {/* Medium screens - show search bar */}
          <div className="hidden md:flex lg:hidden items-center space-x-4">
            <SearchBar className="w-64" />
            {user ? (
              <Button
                onClick={handleSignOut}
                variant="outline"
                size="sm"
              >
                <LogOut className="h-4 w-4" />
              </Button>
            ) : (
              <Button asChild variant="gradient" size="sm">
                <Link to="/auth">
                  <LogIn className="h-4 w-4" />
                </Link>
              </Button>
            )}
          </div>

          {/* Mobile Navigation */}
          <Sheet open={isOpen} onOpenChange={setIsOpen}>
            <SheetTrigger asChild className="md:hidden">
              <Button variant="ghost" size="icon">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[280px] sm:w-[350px]">
              <div className="flex flex-col space-y-4 mt-8">
                <div className="flex items-center justify-between mb-6">
                  <div className="text-2xl font-bold bg-gradient-primary bg-clip-text text-transparent">
                    Small Steps Forward
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={closeSheet}
                    className="h-8 w-8"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
                
                {navItems.map((item) => (
                  <Button
                    key={item.name}
                    asChild
                    variant={isActive(item.path) ? "default" : "ghost"}
                    className={`
                      justify-start h-12 hover:bg-primary/10 hover:text-primary transition-all duration-200
                      ${isActive(item.path) ? "bg-primary/20 text-primary" : ""}
                    `}
                    onClick={closeSheet}
                  >
                    <Link to={item.path} className="flex items-center space-x-3">
                      <item.icon className="h-5 w-5" />
                      <span className="text-base">{item.name}</span>
                    </Link>
                  </Button>
                ))}
                
                {/* Mobile Auth Actions */}
                <div className="border-t border-border/20 pt-4 mt-4">
                  {user ? (
                    <div className="space-y-2">
                      <div className="flex items-center space-x-2 px-3 py-2 text-sm text-muted-foreground">
                        <User className="h-4 w-4" />
                        <span>{user.email}</span>
                      </div>
                      <Button
                        onClick={handleSignOut}
                        variant="outline"
                        size="sm"
                        className="w-full flex items-center justify-center space-x-2"
                      >
                        <LogOut className="h-4 w-4" />
                        <span>Sign Out</span>
                      </Button>
                    </div>
                  ) : (
                    <Button asChild variant="gradient" size="sm" className="w-full">
                      <Link to="/auth" className="flex items-center justify-center space-x-2" onClick={closeSheet}>
                        <LogIn className="h-4 w-4" />
                        <span>Sign In</span>
                      </Link>
                    </Button>
                  )}
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </nav>
  );
};

export default Navigation;